<script setup lang="ts">
  import { onMounted, onUnmounted, ref } from "vue";
  import { IconSuccess, IconClose, IconWarning } from "../Icons";
  import { Text } from "../Text";
  import { NotificationProps } from "./types";

  const { title, description, type = "success", align = "right", duration = 3000, onClose = () => {} } = defineProps<NotificationProps>();

  const isVisible = ref(true);
  let timer: NodeJS.Timeout;

  const close = () => {
    isVisible.value = false;
    onClose();
  };

  onMounted(() => {
    if (duration > 0) {
      timer = setTimeout(close, duration);
    }
  });

  onUnmounted(() => {
    if (timer) {
      clearTimeout(timer);
    }
  });
</script>

<template>
  <Transition name="notification">
    <div v-if="isVisible" :class="['notification', `notification--${type}`, `notification--${align}`]">
      <div class="notification-icon">
        <IconSuccess v-if="type === 'success'" />
        <IconWarning v-if="type === 'warning'" />
        <IconClose v-if="type === 'error'" />
      </div>
      <div class="notification-content">
        <Text :color="type" font="body-regular-14">{{ title }}</Text>
        <Text :color="type" font="body-regular-12">{{ description }}</Text>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
  .notification {
    padding: 11px 16px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: 0px 0px 6px 0px rgba(63, 69, 84, 0.08);
    box-shadow: 0px 8px 12px 0px rgba(63, 69, 84, 0.08);
    z-index: 1000;
    border: 1px;
    border-style: solid;
    width: fit-content;
    position: absolute;
  }

  .notification-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .notification-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    padding: 2px;
  }

  .notification-icon svg {
    width: 20px;
    height: 20px;
  }

  .notification--success {
    background-color: var(--sf-base-background-success);
    border-color: var(--sf-base-border-success);
  }

  .notification--success .notification-icon {
    background-color: var(--sf-toast-background-success);
  }
  .notification--success .notification-icon svg {
    stroke: var(--sf-toast-icon-success);
  }

  .notification--error {
    background-color: var(--sf-base-background-error);
    border-color: var(--sf-base-border-error);
  }

  .notification--error .notification-icon {
    background-color: var(--sf-toast-background-error);
  }
  .notification--error .notification-icon svg {
    stroke: var(--sf-toast-icon-error);
  }

  .notification--warning {
    background-color: var(--sf-base-background-warning);
    border-color: var(--sf-base-border-warning);
  }

  .notification--warning .notification-icon {
    background-color: var(--sf-toast-background-warning);
  }
  .notification--warning .notification-icon svg {
    stroke: var(--sf-toast-icon-warning);
  }

  /* Анимации */
  .notification-enter-active,
  .notification-leave-active {
    transition: all 0.3s ease;
  }

  .notification-enter-from,
  .notification-leave-to {
    opacity: 0;
    transform: translateX(30px);
  }

  .notification--left {
    left: 0;
  }

  .notification--right {
    right: 0;
  }
</style>
